import {
  CONSOLE_HEADER_RESIZE_EVENT,
  EMBEDDED_CHAT_OFFSET_CSS_VAR,
} from 'src/commons/constants';

export const A3C_CONTAINER_ID = 'a3c-embedded-chat-container';
export const DEFAULT_PANEL_WIDTH = 675;
export const NAV_WIDTH = 64;

/**
 * Flyover placement for the embedded chat container, expressed in logical
 * (direction-aware) terms:
 * - 'inline-start': overlays the page next to the nav rail (left in LTR)
 * - 'inline-end': overlays the page anchored to the opposite edge (right in
 *   LTR), matching the legacy A3ChatAssets flyover position
 *
 * When null (default), the panel uses the legacy embedded behavior that
 * pushes the page content aside instead of overlaying it.
 */
export type EmbeddedChatPlacement = 'inline-start' | 'inline-end';

let flyoverPlacement: EmbeddedChatPlacement | null = null;

/**
 * Enables or disables flyover mode for the embedded chat container.
 * In flyover mode the panel overlays the page (no content squeeze).
 * Repositions the container and releases any content margin immediately
 * if the container already exists.
 *
 * The margin handoff is symmetric: entering flyover releases the squeeze,
 * and exiting flyover with the panel visible re-applies it. The exit path
 * matters when a page stamps its embeddedChatConfig after chrome boot
 * (V2350715688): the page is first classified flyover, and when the config
 * lands the placement flips back to embedded — without re-applying the
 * squeeze here the visible panel would keep overlaying the content, since
 * the visibility effect only re-fires when the agent or open state changes.
 */
export function setFlyoverPlacement(
  placement: EmbeddedChatPlacement | null,
): void {
  const wasFlyover = flyoverPlacement !== null;
  flyoverPlacement = placement;
  const container = document.getElementById(A3C_CONTAINER_ID);
  if (container) {
    applyContainerPosition(container, currentFocusMode);
    if (flyoverPlacement) {
      adjustContentMargin(0, currentFocusMode);
    } else if (wasFlyover && container.style.display !== 'none') {
      const width = parseInt(container.style.width) || DEFAULT_PANEL_WIDTH;
      adjustContentMargin(width, currentFocusMode);
      dispatchResizeEvent();
    }
  }
}

/**
 * Returns the current flyover placement (null = legacy push mode).
 */
export function getFlyoverPlacement(): EmbeddedChatPlacement | null {
  return flyoverPlacement;
}

/**
 * Returns true when the document direction is right-to-left.
 */
export function isRTL(): boolean {
  return document.documentElement.dir?.toLowerCase() === 'rtl';
}

/**
 * Dispatches a window resize event so page owners can reflow content.
 */
export function dispatchResizeEvent(): void {
  window.dispatchEvent(new Event('resize'));
}

/**
 * Content area selectors in priority order.
 * - .app-and-pivot-wrapper: ChromeUI-managed layout (dev page)
 * - #sc-content-container: BTSW layout (live ads console pages)
 */
const CONTENT_AREA_SELECTORS = [
  '.app-and-pivot-wrapper',
  '#sc-content-container',
];

/**
 * Finds the main content area element, trying each known selector.
 */
function findContentArea(): HTMLElement | null {
  for (const selector of CONTENT_AREA_SELECTORS) {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) return el;
  }
  return null;
}

/**
 * Watches for the content area to appear so a deferred margin can be applied.
 * Set when adjustContentMargin is called before the page's content area has
 * rendered (chat open-by-default during early page load).
 */
let pendingMarginObserver: MutationObserver | null = null;

/**
 * Cancels any pending deferred margin application.
 */
function cancelPendingMarginRetry(): void {
  pendingMarginObserver?.disconnect();
  pendingMarginObserver = null;
}

/**
 * Resets the pending margin retry state. Only for use in tests.
 */
export function resetPendingMarginRetry(): void {
  cancelPendingMarginRetry();
}

/**
 * Sets the content area margin and width to account for the nav bar and chat panel.
 * Standard mode: margin = NAV_WIDTH + panelWidth
 * Focus mode: margin = panelWidth
 *
 * The footer is outside the content area, so the panel width is also set as
 * EMBEDDED_CHAT_OFFSET_CSS_VAR for it to follow. A CSS variable rather than a
 * style on the footer element: the page template places the footer after the
 * content, so it may not be parsed yet when an open-by-default panel opens.
 *
 * When the panel is closed (panelWidth=0), inline styles are removed so the
 * flex layout (flex: 1) and styled-component margin can fill the space naturally.
 *
 * In flyover mode the panel overlays the page instead of squeezing it, so
 * inline styles are always removed regardless of the panel width.
 *
 * If the content area hasn't rendered yet (chat open-by-default during early
 * page load), a squeeze margin can't be applied — without a retry the panel
 * would overlay the content (reads as flyover) until the next resize. In that
 * case a one-shot MutationObserver waits for the content area and applies the
 * margin as soon as it appears. Any newer adjustContentMargin call supersedes
 * a pending retry.
 */
export function adjustContentMargin(
  panelWidth: number,
  isFocusMode: boolean,
): void {
  cancelPendingMarginRetry();
  const contentArea = findContentArea();
  if (!contentArea) {
    // Only a squeeze margin needs deferring; closing (width removal) and
    // flyover mode leave no styles behind on a not-yet-rendered element.
    if (panelWidth > 0 && !flyoverPlacement) {
      pendingMarginObserver = new MutationObserver(() => {
        if (findContentArea()) {
          adjustContentMargin(panelWidth, isFocusMode);
          dispatchResizeEvent();
        }
      });
      pendingMarginObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    } else {
      document.documentElement.style.removeProperty(
        EMBEDDED_CHAT_OFFSET_CSS_VAR,
      );
    }
    return;
  }

  if (panelWidth > 0 && !flyoverPlacement) {
    const margin = isFocusMode ? panelWidth : NAV_WIDTH + panelWidth;
    contentArea.style.marginInlineStart = `${margin}px`;
    contentArea.style.width = `calc(100% - ${margin}px)`;
    document.documentElement.style.setProperty(
      EMBEDDED_CHAT_OFFSET_CSS_VAR,
      `${panelWidth}px`,
    );
  } else {
    contentArea.style.removeProperty('width');
    contentArea.style.removeProperty('margin-inline-start');
    document.documentElement.style.removeProperty(EMBEDDED_CHAT_OFFSET_CSS_VAR);
  }
}

/**
 * Listens for header resize events and keeps the container's top
 * position in sync so it never sits behind the fixed page header.
 */
export function attachHeaderResizeListener(container: HTMLElement): void {
  document.addEventListener(CONSOLE_HEADER_RESIZE_EVENT, ((e: Event) => {
    const headerHeight = (e as CustomEvent).detail?.headerHeight;
    if (headerHeight != null) {
      container.style.top = `${headerHeight}px`;
    }
  }) as EventListener);
}

/**
 * Applies the correct inline position (left or right) to the container
 * based on the current document direction, focus mode, and flyover placement.
 *
 * - Legacy push mode and flyover 'inline-start': anchored to the inline-start
 *   edge, offset by the nav rail width (0 in focus mode).
 * - Flyover 'inline-end': anchored flush to the inline-end edge, matching the
 *   legacy A3ChatAssets flyover position.
 */
function applyContainerPosition(
  container: HTMLElement,
  isFocusMode: boolean,
): void {
  const anchorToEnd = flyoverPlacement === 'inline-end';
  const offset = anchorToEnd || isFocusMode ? '0' : `${NAV_WIDTH}px`;
  // The physical edge for inline-start is left in LTR and right in RTL;
  // inline-end is the opposite edge.
  const anchorRight = isRTL() ? !anchorToEnd : anchorToEnd;
  if (anchorRight) {
    container.style.right = offset;
    container.style.left = '';
  } else {
    container.style.left = offset;
    container.style.right = '';
  }
}

/**
 * Creates the fixed-position container div for the embedded chat panel.
 * Returns the existing element if it's already in the DOM, updating its
 * position to reflect the current mode and document direction.
 */
export function createEmbeddedChatContainer(isFocusMode: boolean): HTMLElement {
  const existing = document.getElementById(A3C_CONTAINER_ID);
  if (existing) {
    applyContainerPosition(existing, isFocusMode);
    return existing;
  }

  const container = document.createElement('div');
  container.id = A3C_CONTAINER_ID;
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.bottom = '0';
  container.style.zIndex = '299';
  container.style.display = 'none';
  applyContainerPosition(container, isFocusMode);

  document.body.appendChild(container);
  attachHeaderResizeListener(container);
  observeDirectionChanges();
  return container;
}

/**
 * Shows or hides the embedded chat container and adjusts the content margin.
 */
export function setEmbeddedChatVisible(
  visible: boolean,
  defaultWidth: number = DEFAULT_PANEL_WIDTH,
  isFocusMode = false,
): void {
  const container = document.getElementById(A3C_CONTAINER_ID);
  if (container) {
    container.style.display = visible ? 'flex' : 'none';

    if (!container.style.width && visible) {
      container.style.width = `${defaultWidth}px`;
    }
  }

  if (visible) {
    const width = container?.style?.width
      ? parseInt(container.style.width)
      : defaultWidth;
    adjustContentMargin(width, isFocusMode);
  } else {
    adjustContentMargin(0, isFocusMode);
  }

  dispatchResizeEvent();
}

/**
 * Handles a resize of the embedded chat panel by updating the container
 * width and adjusting the content margin. No-ops if the width hasn't
 * changed to prevent infinite resize feedback loops.
 */
export function handleEmbeddedChatResize(
  width: number,
  isFocusMode = false,
): void {
  const container = document.getElementById(A3C_CONTAINER_ID);
  if (!container || container.style.display === 'none') return;
  if (parseInt(container.style.width) === width) return;

  container.style.width = `${width}px`;
  adjustContentMargin(width, isFocusMode);
  dispatchResizeEvent();
}

/**
 * Module-level focus mode state so callbacks always use the current value.
 */
let currentFocusMode = false;

/**
 * Tracks whether the direction observer has been attached (once per page).
 */
let directionObserverAttached = false;

/**
 * Reference to the active direction observer for cleanup.
 */
let directionObserver: MutationObserver | null = null;

/**
 * Observes changes to document.documentElement.dir and repositions the
 * embedded chat container automatically. Prevents layout shift when the
 * locale is switched at runtime while the panel is visible.
 */
export function observeDirectionChanges(): void {
  if (directionObserverAttached) return;
  directionObserverAttached = true;

  directionObserver = new MutationObserver(() => {
    const container = document.getElementById(A3C_CONTAINER_ID);
    if (container) {
      applyContainerPosition(container, currentFocusMode);
    }
  });

  directionObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['dir'],
  });
}

/**
 * Updates the stored focus mode state. Call this whenever the mode changes.
 */
export function setCurrentFocusMode(isFocusMode: boolean): void {
  currentFocusMode = isFocusMode;
}

/**
 * Resets the direction observer state. Only for use in tests.
 */
export function resetDirectionObserver(): void {
  directionObserver?.disconnect();
  directionObserver = null;
  directionObserverAttached = false;
}

interface InitA3CEmbeddedConfigOptions {
  closable: boolean;
  onStateChange?: (isOpen: boolean) => void;
  selectedAgentIdentifier?: string;
}

/**
 * Initializes window.A3CConfig with embedded mode settings.
 * Merges with any existing config (preserving page owner's panelSizes).
 * Callbacks reference the module-level focus mode so they never go stale.
 */
export function initA3CEmbeddedConfig({
  closable,
  onStateChange,
  selectedAgentIdentifier,
}: InitA3CEmbeddedConfigOptions): void {
  const existing = (window as any).A3CConfig || {};
  (window as any).A3CConfig = {
    ...existing,
    containerId: A3C_CONTAINER_ID,
    closable,
    selectedAgentIdentifier,
    onClose: () => {
      setEmbeddedChatVisible(false, DEFAULT_PANEL_WIDTH, currentFocusMode);
      onStateChange?.(false);
    },
    onResize: (width: number) => {
      handleEmbeddedChatResize(width, currentFocusMode);
    },
  };
}
